import React, { useState, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { addcomponent, iconsPath } from '../../utils/imagePath';
import {
  EnhancedSlider,
  GridDropdown,
  GridInputWithIcon,
  ToggleCheckbox,
  ColorInput,
} from '../../utils/gridUtils';
import { handleVideoUpload, attachVideoTextureToObject, applyVideoTextureToObject } from '../gridpagefunction/Video';
import store from '../../redux/store';
import { useSelector } from 'react-redux';
import {
  addSceneVideo,
  setPushIntersectedObject,
  selected_object as setSelectedObjectRedux,
  setVideoScales,
} from '../../redux/Slice/gridSlice';
import { calculateVideoPlaneDimensions } from '../../utils/videoScaleUtils';
import { applyStoredPivotToObject } from '../../utils/ThreeJsUtils/pivotEditor';

const EMPTY_VIDEO_CLIP = 'Upload clip';

const VideoComponent = ({
  completeVideoObject,
  scene,
  transformControls,
  setPolyCount,
  setPendingUploads,
}) => {
  const window_type = useSelector((state) => state.window?.window_type);
  const [selectedObject, setSelectedObject] = useState(
    completeVideoObject?.selectedObject
  );
  const videoElementRef = useRef(null);
  const videoTextureRef = useRef(null);
  const fileInputRef = useRef(null);
  const animationFrameRef = useRef(null);
  const createdMeshRef = useRef(null); // Track created mesh for URL videos
  const videoClipRef = useRef(
    selectedObject?.userData?.video_settings?.video_clip ||
      (selectedObject?.deleteType === 'video'
        ? selectedObject?.objectURL
        : null) ||
      EMPTY_VIDEO_CLIP
  );

  const extractVideoUrl = () => {
    if (!selectedObject) return EMPTY_VIDEO_CLIP;
    if (selectedObject?.userData?.video_settings?.video_clip) {
      return selectedObject.userData.video_settings.video_clip;
    }
    if (selectedObject?.deleteType !== 'video') return EMPTY_VIDEO_CLIP;
    if (selectedObject?.object?.name) return selectedObject.object.name;
    if (selectedObject?.asset_file_path) return selectedObject.asset_file_path;
    if (selectedObject?.objectURL) return selectedObject.objectURL;
    return EMPTY_VIDEO_CLIP;
  };

  const [hydratedObject, setHydratedObject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [value, setValue] = useState(
    selectedObject?.userData?.video_settings?.playback_speed ?? 1
  );
  const [playOnAwakeChecked, setPlayOnAwakeChecked] = useState(
    selectedObject?.userData?.video_settings?.play_on_awake ?? true
  );
  const [waitForFirstFrameChecked, setWaitForFirstFrameChecked] = useState(
    selectedObject?.userData?.video_settings?.wait_for_first_frame ?? false
  );
  const [loopChecked, setLoopChecked] = useState(
    selectedObject?.userData?.video_settings?.loop ?? false
  );
  const [muteChecked, setMuteChecked] = useState(
    selectedObject?.userData?.video_settings?.mute ?? false
  );

  const [playbackState, setPlaybackState] = useState(
    selectedObject?.userData?.video_settings?.playback_state ?? 'Play'
  );
  const [source, setSource] = useState(
    selectedObject?.userData?.video_settings?.source ?? 'Uploaded Clip'
  );
  const [videoClip, setVideoClip] = useState(
    selectedObject?.userData?.video_settings?.video_clip ||
      (selectedObject?.deleteType === 'video'
        ? selectedObject?.objectURL
        : null) ||
      extractVideoUrl()
  );
  const [renderMode, setRenderMode] = useState(
    selectedObject?.userData?.video_settings?.render_mode ?? 'Render Texture'
  );
  const [aspectRatio, setAspectRatio] = useState(
    selectedObject?.userData?.video_settings?.aspect_ratio ?? 'Fit Horizontally'
  );
  const [volume, setVolume] = useState(
    selectedObject?.userData?.video_settings?.volume ?? 0.5
  );

  // Chroma Key state
  const [chromaKeyEnabled, setChromaKeyEnabled] = useState(
    selectedObject?.userData?.video_settings?.chroma_key_enabled ?? false
  );
  const [chromaKeyColor, setChromaKeyColor] = useState(
    selectedObject?.userData?.video_settings?.chroma_key_color ?? 'Green'
  );
  const [chromaKeyCustomColor, setChromaKeyCustomColor] = useState(
    selectedObject?.userData?.video_settings?.chroma_key_custom_color ??
    '#00FF00'
  );
  const [chromaKeySimilarity, setChromaKeySimilarity] = useState(
    selectedObject?.userData?.video_settings?.chroma_key_similarity ?? 0.01
  );
  const [chromaKeySmoothness, setChromaKeySmoothness] = useState(
    selectedObject?.userData?.video_settings?.chroma_key_smoothness ?? 0
  );
  const [chromaKeySpill, setChromaKeySpill] = useState(
    selectedObject?.userData?.video_settings?.chroma_key_spill ?? 0
  );

  useEffect(() => {
    videoClipRef.current = videoClip;
  }, [videoClip]);

  useEffect(() => {
    if (completeVideoObject?.selectedObject) {
      setSelectedObject(completeVideoObject.selectedObject);
    }
  }, [completeVideoObject]);

  useEffect(() => {
    const hydrateObject = async () => {
      if (!selectedObject?.object || selectedObject._hydrated) return;

      try {
        setIsLoading(true);
        setError(null);

        const loader = new THREE.ObjectLoader();
        const hydrated = loader.parse(selectedObject);

        if (!hydrated) {
          throw new Error('Failed to hydrate object');
        }

        hydrated._hydrated = true;
        setHydratedObject(hydrated);
        setSelectedObject(hydrated);
      } catch (err) {
        console.error('❌ Error hydrating object:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    hydrateObject();
  }, [selectedObject]);

  useEffect(() => {
    if (!selectedObject) return;

    // Find the actual scene object to bypass potential Redux proxy read-only limitations
    const actualObject =
      scene.getObjectByName(selectedObject.name || selectedObject._id) ||
      selectedObject;

    if (!actualObject.userData || Object.isFrozen(actualObject.userData)) {
      try {
        actualObject.userData = { ...(actualObject.userData || {}) };
      } catch (e) {
        console.warn('Could not initialize or clone userData:', e);
      }
    }

    if (
      actualObject.userData.video_settings &&
      Object.isFrozen(actualObject.userData.video_settings)
    ) {
      try {
        actualObject.userData.video_settings = {
          ...actualObject.userData.video_settings,
        };
      } catch (e) {
        console.warn('Could not clone video_settings:', e);
      }
    }

    if (!actualObject.userData.video_settings) {
      const videoUrl = extractVideoUrl();
      const defaultSettings = {
        source: 'Uploaded Clip',
        video_clip: videoUrl,
        playback_speed: 1,
        playback_state: 'Play',
        play_on_awake: false,
        wait_for_first_frame: false,
        loop: false,
        mute: false,
        render_mode: 'Render Texture',
        aspect_ratio: 'Fit Horizontally',
        volume: 0.5,
        chroma_key_enabled: false,
        chroma_key_color: 'Green',
        chroma_key_custom_color: '#00FF00',
        chroma_key_similarity: 0.01,
        chroma_key_smoothness: 0,
        chroma_key_spill: 0,
      };

      try {
        // Use Object.defineProperty if direct assignment fails, or just ensure it's writable
        Object.defineProperty(actualObject.userData, 'video_settings', {
          value: defaultSettings,
          writable: true,
          configurable: true,
          enumerable: true
        });
      } catch (e) {
        // Fallback to direct assignment if defineProperty fails (e.g. if already defined but non-configurable)
        try {
          actualObject.userData.video_settings = defaultSettings;
        } catch (innerError) {
          console.warn('Final fallback for video_settings initialization failed:', innerError);
        }
      }
    }
  }, [selectedObject]);

  useEffect(() => {
    if (!selectedObject?.userData?.video_settings) return;
    setValue(selectedObject.userData.video_settings.playback_speed ?? 1);
    setVolume(selectedObject.userData.video_settings.volume ?? 0.5);
    const playOnAwakeVal = selectedObject.userData.video_settings.play_on_awake;
    setPlayOnAwakeChecked(playOnAwakeVal === true || String(playOnAwakeVal) === 'true');
    const waitVal = selectedObject.userData.video_settings.wait_for_first_frame;
    setWaitForFirstFrameChecked(waitVal === true || String(waitVal) === 'true');
    const loopVal = selectedObject.userData.video_settings.loop;
    setLoopChecked(loopVal === true || String(loopVal) === 'true');
    const muteVal = selectedObject.userData.video_settings.mute;
    setMuteChecked(muteVal === true || String(muteVal) === 'true');
    setPlaybackState(
      selectedObject.userData.video_settings.playback_state ?? 'Play'
    );
    setSource(selectedObject.userData.video_settings.source ?? 'Uploaded Clip');
    setVideoClip(
      selectedObject.userData.video_settings.video_clip ||
        (selectedObject?.deleteType === 'video'
          ? selectedObject?.objectURL
          : null) ||
        extractVideoUrl()
    );
    setRenderMode(
      selectedObject.userData.video_settings.render_mode ?? 'Render Texture'
    );
    setAspectRatio(
      selectedObject.userData.video_settings.aspect_ratio ?? 'Fit Horizontally'
    );
    setChromaKeyEnabled(
      selectedObject.userData.video_settings.chroma_key_enabled ?? false
    );
    setChromaKeyColor(
      selectedObject.userData.video_settings.chroma_key_color ?? 'Green'
    );
    setChromaKeyCustomColor(
      selectedObject.userData.video_settings.chroma_key_custom_color ??
      '#00FF00'
    );
    setChromaKeySimilarity(
      selectedObject.userData.video_settings.chroma_key_similarity ?? 0.01
    );
    setChromaKeySmoothness(
      selectedObject.userData.video_settings.chroma_key_smoothness ?? 0
    );
    setChromaKeySpill(
      selectedObject.userData.video_settings.chroma_key_spill ?? 0
    );
  }, [selectedObject]);

  useEffect(() => {
    if (!selectedObject) return;
    try {
      // Find the actual scene object
      const actualObject =
        scene.getObjectByName(selectedObject.name || selectedObject._id) ||
        selectedObject;

      // Ensure userData and video_settings are writable
      if (!actualObject.userData || Object.isFrozen(actualObject.userData)) {
        actualObject.userData = { ...(actualObject.userData || {}) };
      }
      if (
        actualObject.userData.video_settings &&
        Object.isFrozen(actualObject.userData.video_settings)
      ) {
        actualObject.userData.video_settings = {
          ...actualObject.userData.video_settings,
        };
      }

      const newSettings = {
        source,
        video_clip: videoClip,
        playback_speed: value,
        volume: volume,
        playback_state: playbackState,
        play_on_awake: playOnAwakeChecked,
        wait_for_first_frame: waitForFirstFrameChecked,
        loop: loopChecked,
        mute: muteChecked,
        render_mode: renderMode,
        aspect_ratio: aspectRatio,
        chroma_key_enabled: chromaKeyEnabled,
        chroma_key_color: chromaKeyColor,
        chroma_key_custom_color: chromaKeyCustomColor,
        chroma_key_similarity: chromaKeySimilarity,
        chroma_key_smoothness: chromaKeySmoothness,
        chroma_key_spill: chromaKeySpill,
      };

      try {
        // Use Object.defineProperty to ensure the property is writable even if the object was previously proxied
        if (Object.isFrozen(actualObject.userData)) {
           console.warn('userData is frozen, cannot update video_settings directly');
        } else {
           actualObject.userData.video_settings = newSettings;
        }
      } catch (assignError) {
        console.warn('Direct assignment failed, trying defineProperty', assignError);
        try {
          Object.defineProperty(actualObject.userData, 'video_settings', {
            value: newSettings,
            writable: true,
            configurable: true,
            enumerable: true
          });
        } catch (defError) {
           console.error('All attempts to update video_settings failed:', defError);
        }
      }
    } catch (err) {
      console.error('❌ Error updating video settings:', err);
      setError(err.message);
    }


  }, [
    selectedObject,
    source,
    videoClip,
    value,
    playbackState,
    playOnAwakeChecked,
    waitForFirstFrameChecked,
    loopChecked,
    muteChecked,
    renderMode,
    aspectRatio,
    volume,
    chromaKeyEnabled,
    chromaKeyColor,
    chromaKeyCustomColor,
    chromaKeySimilarity,
    chromaKeySmoothness,
    chromaKeySpill,
  ]);

  // Create chroma key shader material - using production-ready shader from reference
  const createChromaKeyMaterial = (videoTexture) => {
    // Get the chroma key color from the color picker
    // Always use chromaKeyCustomColor since it's synced with presets
    let chromaColor = new THREE.Color(0x00ff00); // Default green
    try {
      if (chromaKeyCustomColor) {
        chromaColor = new THREE.Color(chromaKeyCustomColor);
      } else if (chromaKeyColor === 'Blue') {
        chromaColor = new THREE.Color(0x0000ff);
      } else if (chromaKeyColor === 'Green') {
        chromaColor = new THREE.Color(0x00ff00);
      }
    } catch (err) {
      console.warn('Invalid chroma key color, using default green:', err);
      chromaColor = new THREE.Color(0x00ff00);
    }

    // Exact shader from threejs_chromakey_video_material reference
    const vertexShader = `
      varying vec2 vUv;

      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
      }
    `;

    const fragmentShader = `
      uniform sampler2D tex;
      uniform vec3 keyColor;
      uniform float similarity;
      uniform float smoothness;
      uniform float spill;

      varying vec2 vUv;

      // From https://github.com/libretro/glsl-shaders/blob/master/nnedi3/shaders/rgb-to-yuv.glsl
      vec2 RGBtoUV(vec3 rgb) {
        return vec2(
          rgb.r * -0.169 + rgb.g * -0.331 + rgb.b *  0.5    + 0.5,
          rgb.r *  0.5   + rgb.g * -0.419 + rgb.b * -0.081  + 0.5
        );
      }

      vec4 ProcessChromaKey(vec2 texCoord) {
        vec4 rgba = texture2D(tex, texCoord);
        float chromaDist = distance(RGBtoUV(texture2D(tex, texCoord).rgb), RGBtoUV(keyColor));

        float baseMask = chromaDist - similarity;
        float fullMask = pow(clamp(baseMask / smoothness, 0., 1.), 1.5);
        rgba.a = fullMask;

        float spillVal = pow(clamp(baseMask / spill, 0., 1.), 1.5);
        float desat = clamp(rgba.r * 0.2126 + rgba.g * 0.7152 + rgba.b * 0.0722, 0., 1.);
        rgba.rgb = mix(vec3(desat, desat, desat), rgba.rgb, spillVal);

        return rgba;
      }

      void main(void) {
        vec2 texCoord = vUv;
        gl_FragColor = ProcessChromaKey(texCoord);
      }
    `;

    const material = new THREE.ShaderMaterial({
      uniforms: {
        tex: { value: videoTexture },
        keyColor: { value: chromaColor },
        similarity: { value: chromaKeySimilarity },
        smoothness: { value: chromaKeySmoothness },
        spill: { value: chromaKeySpill },
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      side: THREE.DoubleSide,
    });

    return material;
  };

  const cleanupVideo = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Only clear the video element if no mesh in the scene is still using it
    // (otherwise we'd break the video thumbnail when user deselects or switches tab).
    if (videoElementRef.current && scene) {
      let elementStillInUse = false;
      scene.traverse((obj) => {
        if (obj.videoElement === videoElementRef.current) {
          elementStillInUse = true;
        }
      });
      if (!elementStillInUse) {
        videoElementRef.current.pause();
        // Clear src before load() to stop the video completely
        // The error listener in Video.js will now ignore this
        videoElementRef.current.src = '';
        videoElementRef.current.load();
      }
      videoElementRef.current = null;
    }

    // Only dispose the texture if no mesh in the scene is still using it.
    // When user attaches transform controls to a video and then detaches (or switches tab),
    // this cleanup can run while the video mesh is still in the scene; disposing the
    // shared texture would make the video thumbnail disappear.
    if (videoTextureRef.current && scene) {
      let textureStillInUse = false;
      scene.traverse((obj) => {
        if (obj.isMesh && obj.material) {
          const materials = Array.isArray(obj.material)
            ? obj.material
            : [obj.material];
          for (const mat of materials) {
            if (mat.map === videoTextureRef.current) {
              textureStillInUse = true;
              return;
            }
          }
        }
      });
      if (!textureStillInUse) {
        videoTextureRef.current.dispose();
      }
      videoTextureRef.current = null;
    }

    // Remove previously created mesh from scene
    if (createdMeshRef.current && scene) {
      scene.remove(createdMeshRef.current);

      if (createdMeshRef.current.geometry) {
        createdMeshRef.current.geometry.dispose();
      }
      if (createdMeshRef.current.material) {
        if (createdMeshRef.current.material.map) {
          createdMeshRef.current.material.map.dispose();
        }
        createdMeshRef.current.material.dispose();
      }
      if (createdMeshRef.current.videoElement) {
        createdMeshRef.current.videoElement.pause();
        createdMeshRef.current.videoElement.removeAttribute('src');
        createdMeshRef.current.videoElement.load();
      }
      if (createdMeshRef.current.videoTexture) {
        createdMeshRef.current.videoTexture.dispose();
      }

      createdMeshRef.current = null;
    }
  };

  useEffect(() => {
    if (!videoClip || videoClip === EMPTY_VIDEO_CLIP) return;

    const targetObject = hydratedObject || selectedObject;
    const canApplyToSelected =
      targetObject &&
      (targetObject.deleteType === 'video' ||
        targetObject.isMesh ||
        typeof targetObject.traverse === 'function');

    // URL source can create a new plane; uploaded clips apply to the selected object
    if (source !== 'URL' && !canApplyToSelected) {
      return;
    }

    // --- NEW LOGIC TO REUSE EXISTING VIDEO ---
    // If the object already has a video playing the exact same clip, do not recreate!
    if (
      targetObject &&
      targetObject.videoElement &&
      targetObject.videoTexture &&
      (targetObject.userData?.video_settings?.video_clip === videoClip ||
        targetObject.asset_file_path === videoClip ||
        targetObject.objectURL === videoClip)
    ) {
      // Hijack existing instances so UI updates apply to them!
      videoElementRef.current = targetObject.videoElement;
      videoTextureRef.current = targetObject.videoTexture;

      // Ensure the material continues to animate the texture
      const updateTexture = () => {
        if (videoTextureRef.current && videoElementRef.current) {
          videoTextureRef.current.needsUpdate = true;
          animationFrameRef.current = requestAnimationFrame(updateTexture);
        }
      };

      // Stop old animation frames if they existed locally
      if (animationFrameRef.current)
        cancelAnimationFrame(animationFrameRef.current);
      updateTexture();

      // Ensure chroma key state is synced since it's a re-mount
      if (targetObject.material) {
        if (chromaKeyEnabled && !targetObject.material.isShaderMaterial) {
          if (targetObject.material.dispose) targetObject.material.dispose();
          targetObject.material = createChromaKeyMaterial(
            videoTextureRef.current
          );
          targetObject.material.needsUpdate = true;
        } else if (
          !chromaKeyEnabled &&
          targetObject.material.isShaderMaterial
        ) {
          if (targetObject.material.dispose) targetObject.material.dispose();
          targetObject.material = new THREE.MeshStandardMaterial({
            map: videoTextureRef.current,
            side: THREE.DoubleSide,
            transparent: false,
            opacity: 1.0,
          });
          targetObject.material.needsUpdate = true;
        }
      }

      // Restore playback sync queue since we bypassed it
      const syncPlayState = () => {
        const isBlockedByPlaybackState =
          playbackState === 'Pause' || playbackState === 'Stop';
        if (!isBlockedByPlaybackState && (playbackState === 'Play' || playOnAwakeChecked)) {
          targetObject.videoElement.play().catch(console.warn);
        } else {
          targetObject.videoElement.pause();
          if (playbackState === 'Stop') targetObject.videoElement.currentTime = 0;
        }
      };

      if (targetObject.videoElement.readyState >= 2) {
        syncPlayState();
      } else {
        targetObject.videoElement.addEventListener(
          'loadeddata',
          syncPlayState,
          { once: true }
        );
      }

      return; // Exit early, skipping cleanup & recreation!
    }
    // ------------------------------------------

    // CRITICAL: Clean up previous video BEFORE creating new one
    cleanupVideo();

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.src = videoClip;
    video.playsInline = true;
    video.autoplay = false;
    video.loop = loopChecked;
    video.muted = muteChecked;
    video.playbackRate = value;
    video.volume = volume;
    video.preload = 'auto';

    video.onerror = (e) => {
      // Ignore errors when src is cleared during cleanup
      if (!video.src || video.src === window.location.href) return;
      setError(
        `Error loading video: ${e.target.error?.message || 'Unknown error'}`
      );
    };

    videoElementRef.current = video;

    // Create video texture
    const videoTexture = new THREE.VideoTexture(video);
    videoTextureRef.current = videoTexture;
    videoTexture.minFilter = THREE.LinearFilter;
    videoTexture.magFilter = THREE.LinearFilter;
    videoTexture.format = THREE.RGBFormat;
    videoTexture.generateMipmaps = false;

    // Store references on selected object if it exists
    if (selectedObject) {
      selectedObject.videoElement = video;
      selectedObject.videoTexture = videoTexture;
    }
    // Apply texture to existing video plane OR any selected mesh/group
    if (targetObject && (targetObject.deleteType === 'video' || targetObject.isMesh || typeof targetObject.traverse === 'function')) {
      if (chromaKeyEnabled) {
        const applyChroma = (mesh) => {
          if (!mesh?.isMesh || !mesh.material) return;
          try {
            mesh.material?.dispose?.();
          } catch (_) {
            /* ignore */
          }
          mesh.material = createChromaKeyMaterial(videoTexture);
          mesh.material.needsUpdate = true;
        };
        if (targetObject.isMesh) applyChroma(targetObject);
        else targetObject.traverse?.((child) => applyChroma(child));
      } else {
        applyVideoTextureToObject(targetObject, videoTexture, THREE);
      }

      const updateTexture = () => {
        if (videoTexture && videoElementRef.current) {
          videoTexture.needsUpdate = true;
          animationFrameRef.current = requestAnimationFrame(updateTexture);
        }
      };

      video.addEventListener(
        'loadeddata',
        () => {
          updateTexture();

          const isAutoPlay = playOnAwakeChecked === true || String(playOnAwakeChecked) === 'true';
          const isBlockedByPlaybackState =
            playbackState === 'Pause' || playbackState === 'Stop';
          if (
            !isBlockedByPlaybackState &&
            (playbackState === 'Play' || (window_type === 'design' && isAutoPlay))
          ) {
            video.play().catch(console.warn);
          }
        },
        { once: true }
      );

      video.load();
    } else if (scene && source === 'URL') {
      // Create new plane for URL videos
      const createPlaneWithVideo = () => {


        const videoAspect =
          video.videoWidth && video.videoHeight
            ? video.videoWidth / video.videoHeight
            : 1;

        // Calculate plane dimensions based on aspect ratio setting
        const { planeWidth, planeHeight } = calculateVideoPlaneDimensions(
          videoAspect,
          aspectRatio
        );

        const geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);
        // Use chroma key material if enabled, otherwise use standard material
        const material = chromaKeyEnabled
          ? createChromaKeyMaterial(videoTexture)
          : new THREE.MeshStandardMaterial({
            map: videoTexture,
            side: THREE.DoubleSide,
            transparent: false,
            opacity: 1.0,
          });

        const mesh = new THREE.Mesh(geometry, material);
        mesh.deleteType = 'video';
        mesh.asset_file_path = videoClip;
        mesh.asset_file_pathh = videoClip; // Keep for compatibility
        mesh.name = `video-${Date.now()}`;
        mesh.videoElement = video;
        mesh.videoTexture = videoTexture;

        mesh.userData = {
          video_settings: {
            source: 'URL',
            video_clip: videoClip,
            playback_speed: value,
            volume: volume,
            playback_state: playbackState,
            play_on_awake: playOnAwakeChecked,
            wait_for_first_frame: waitForFirstFrameChecked,
            loop: loopChecked,
            mute: muteChecked,
            render_mode: renderMode,
            aspect_ratio: aspectRatio,
            base_plane_width: planeWidth,
            base_plane_height: planeHeight,
            chroma_key_enabled: chromaKeyEnabled,
            chroma_key_color: chromaKeyColor,
            chroma_key_custom_color: chromaKeyCustomColor,
            chroma_key_similarity: chromaKeySimilarity,
            chroma_key_smoothness: chromaKeySmoothness,
            chroma_key_spill: chromaKeySpill,
          },
        };

        scene.add(mesh);
        createdMeshRef.current = mesh; // Track the created mesh
        setSelectedObject(mesh);

        store.dispatch(setVideoScales({ x: planeWidth, y: planeHeight }));
        store.dispatch(setPushIntersectedObject(mesh));
        store.dispatch(addSceneVideo(mesh));
        store.dispatch(setSelectedObjectRedux(mesh));

        const updateTexture = () => {
          if (videoTexture && !mesh.userData.disposed) {
            videoTexture.needsUpdate = true;
            animationFrameRef.current = requestAnimationFrame(updateTexture);
          }
        };

        video.addEventListener(
          'loadeddata',
          () => {
            updateTexture();

            const isBlockedByPlaybackState =
              playbackState === 'Pause' || playbackState === 'Stop';
            if (!isBlockedByPlaybackState && (playbackState === 'Play' || playOnAwakeChecked)) {
              video.play().catch(console.warn);
            }
          },
          { once: true }
        );

        video.load();
      };

      // Check if video dimensions are already available
      if (
        Number.isFinite(video.videoWidth) &&
        Number.isFinite(video.videoHeight) &&
        video.videoWidth > 0 &&
        video.videoHeight > 0
      ) {
        createPlaneWithVideo();
      } else {
        // Wait for metadata to load
        video.addEventListener('loadedmetadata', createPlaneWithVideo, {
          once: true,
        });
      }
    }

    // CRITICAL: Force update all other materials to prevent black textures
    if (scene) {
      scene.traverse((child) => {
        if (child.isMesh && child.material && child !== targetObject) {
          if (Array.isArray(child.material)) {
            child.material.forEach((mat) => {
              if (mat.map) mat.map.needsUpdate = true;
              mat.needsUpdate = true;
            });
          } else {
            if (child.material.map) child.material.map.needsUpdate = true;
            child.material.needsUpdate = true;
          }
        }
      });
    }

    return () => {
      // Cleanup will happen in main cleanup effect
    };
  }, [videoClip]);

  useEffect(() => {
    if (!videoElementRef.current) return;
    videoElementRef.current.loop = loopChecked;
  }, [loopChecked]);

  useEffect(() => {
    if (!videoElementRef.current) return;
    videoElementRef.current.muted = muteChecked;
  }, [muteChecked]);

  useEffect(() => {
    if (!videoElementRef.current) return;
    videoElementRef.current.playbackRate = value;
  }, [value]);

  useEffect(() => {
    if (!videoElementRef.current) return;
    videoElementRef.current.volume = volume;
  }, [volume]);

  // Update plane geometry when aspect ratio changes
  useEffect(() => {
    if (!selectedObject || !videoElementRef.current) return;
    const targetObject = hydratedObject || selectedObject;

    // Only update if it's a video plane
    if (
      targetObject &&
      targetObject.isMesh &&
      targetObject.deleteType === 'video' &&
      targetObject.geometry
    ) {
      if (
        !videoElementRef.current.videoWidth ||
        !videoElementRef.current.videoHeight
      ) {
        return;
      }

      const videoAspect =
        videoElementRef.current.videoWidth / videoElementRef.current.videoHeight;

      const settings = targetObject.userData?.video_settings || {};
      // Prefer the authored plane size so select/hydrate does not rebuild geometry
      // and wipe an edited pivot offset.
      const { planeWidth, planeHeight } =
        settings.base_plane_width > 0 && settings.base_plane_height > 0
          ? {
              planeWidth: settings.base_plane_width,
              planeHeight: settings.base_plane_height,
            }
          : calculateVideoPlaneDimensions(videoAspect, aspectRatio);

      const prevParams = targetObject.geometry?.parameters;
      const samePlane =
        prevParams &&
        Math.abs((prevParams.width || 0) - planeWidth) < 1e-6 &&
        Math.abs((prevParams.height || 0) - planeHeight) < 1e-6;

      // Selecting the video should not recreate geometry if the plane size is unchanged
      // (rebuilding a centered PlaneGeometry would wipe the pivot offset).
      if (samePlane) {
        if (
          targetObject.userData &&
          settings.aspect_ratio !== aspectRatio
        ) {
          targetObject.userData.video_settings = {
            ...settings,
            aspect_ratio: aspectRatio,
          };
        }
        return;
      }

      const worldWidth =
        prevParams?.width > 0
          ? prevParams.width * targetObject.scale.x
          : targetObject.scale.x;
      const worldHeight =
        prevParams?.height > 0
          ? prevParams.height * targetObject.scale.y
          : targetObject.scale.y;

      // Dispose old geometry and create new one
      if (targetObject.geometry) {
        targetObject.geometry.dispose();
      }
      targetObject.geometry = new THREE.PlaneGeometry(planeWidth, planeHeight);

      targetObject.scale.set(
        worldWidth / planeWidth,
        worldHeight / planeHeight,
        targetObject.scale.z
      );

      // Ensure targetObject.userData is writable
      if (!targetObject.userData || Object.isFrozen(targetObject.userData)) {
        try {
          targetObject.userData = { ...(targetObject.userData || {}) };
        } catch (e) {
          console.warn('Could not clone targetObject.userData:', e);
        }
      }
      if (
        targetObject.userData &&
        targetObject.userData.video_settings &&
        Object.isFrozen(targetObject.userData.video_settings)
      ) {
        try {
          targetObject.userData.video_settings = {
            ...targetObject.userData.video_settings,
          };
        } catch (e) {
          console.warn('Could not clone targetObject.userData.video_settings:', e);
        }
      }

      if (targetObject.userData) {
        targetObject.userData.video_settings = {
          ...settings,
          aspect_ratio: aspectRatio,
          base_plane_width: planeWidth,
          base_plane_height: planeHeight,
        };
      }

      // PlaneGeometry rebuild is centered — restore authored pivot offset.
      applyStoredPivotToObject(targetObject, targetObject);

      // Update stored scales in Redux
      store.dispatch(setVideoScales({ x: planeWidth, y: planeHeight }));
    }
  }, [aspectRatio, selectedObject, hydratedObject]);

  useEffect(() => {
    if (!videoElementRef.current) return;
    if (playbackState === 'Play') {
      videoElementRef.current.play().catch(console.warn);
    } else if (playbackState === 'Pause') {
      videoElementRef.current.pause();
    } else if (playbackState === 'Stop') {
      videoElementRef.current.pause();
      videoElementRef.current.currentTime = 0;
    }
  }, [playbackState]);

  // Update chroma key material when settings change
  useEffect(() => {
    if (!selectedObject || !videoTextureRef.current) return;
    const targetObject = hydratedObject || selectedObject;

    if (
      targetObject &&
      (targetObject.deleteType === 'video' ||
        targetObject.isMesh ||
        typeof targetObject.traverse === 'function')
    ) {
      applyVideoTextureToObject(
        targetObject,
        videoTextureRef.current,
        THREE
      );
      if (chromaKeyEnabled) {
        const applyChroma = (mesh) => {
          if (!mesh?.isMesh || !mesh.material) return;
          try {
            mesh.material?.dispose?.();
          } catch (_) {
            /* ignore */
          }
          mesh.material = createChromaKeyMaterial(videoTextureRef.current);
          mesh.material.needsUpdate = true;
        };
        if (targetObject.isMesh) applyChroma(targetObject);
        else targetObject.traverse?.((child) => applyChroma(child));
      }
    }
  }, [
    chromaKeyEnabled,
    chromaKeyColor,
    chromaKeyCustomColor,
    chromaKeySimilarity,
    chromaKeySmoothness,
    chromaKeySpill,
    selectedObject,
    hydratedObject,
  ]);

  // Stop all other videos when a video is selected
  useEffect(() => {
    if (!selectedObject || !scene) return;

    scene.traverse((child) => {
      // Skip the currently selected video
      if (child === selectedObject || child === hydratedObject) return;

      // Stop any other video that's currently playing
      if (child.videoElement) {
        child.videoElement.pause();
        child.videoElement.currentTime = 0;
      }
    });
  }, [selectedObject, hydratedObject, scene]);

  useEffect(() => {
    const onDeleteMesh = () => {
      requestAnimationFrame(() => {
        const clip = videoClipRef.current;
        if (!clip || clip === EMPTY_VIDEO_CLIP) return;

        let clipStillUsed = false;
        scene?.traverse((child) => {
          if (child.deleteType !== 'video') return;
          if (
            child.asset_file_path === clip ||
            child.objectURL === clip ||
            child.userData?.video_settings?.video_clip === clip
          ) {
            clipStillUsed = true;
          }
        });

        if (!clipStillUsed) {
          // Stop the video element immediately so audio does not continue
          if (videoElementRef.current) {
            try {
              videoElementRef.current.pause();
              videoElementRef.current.src = '';
              videoElementRef.current.load();
            } catch (_) {}
          }
          if (typeof clip === 'string' && clip.startsWith('blob:')) {
            URL.revokeObjectURL(clip);
          }
          setVideoClip(EMPTY_VIDEO_CLIP);
        }
      });
    };

    window.addEventListener('deleteMesh', onDeleteMesh);
    return () => window.removeEventListener('deleteMesh', onDeleteMesh);
  }, [scene]);

  useEffect(() => {
    return () => {
      cleanupVideo();
    };
  }, []);

  const handleVideoUploadClick = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const target =
      completeVideoObject?.selectedObject || selectedObject || hydratedObject;
    const shouldAttachToObject =
      target &&
      target.deleteType !== 'video' &&
      (target.isMesh ||
        target.deleteType === 'mesh' ||
        target.glb_type === 'shapes' ||
        typeof target.traverse === 'function');

    if (shouldAttachToObject) {
      attachVideoTextureToObject(file, target, {
        Three: THREE,
        scene,
        setPolyCount,
        setPendingUploads,
      })
        .then(({ objectURL, tempId }) => {
          setSource('Uploaded Clip');
          setVideoClip(objectURL);
          setError(null);
          setSelectedObject(target);
        })
        .catch((err) => {
          setError(err?.message || 'Failed to attach video texture');
        });
      event.target.value = '';
      return;
    }

    handleVideoUpload(
      event,
      {
        Three: THREE,
        scene,
        transformControls,
        selectedObject,
        setPolyCount,
      },
      setPendingUploads
    );

    event.target.value = '';
  };

  const handleIconClick = () => {
    if (source !== 'Uploaded Clip') return;
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleVideoUploadClick}
        style={{ display: 'none' }}
      />

      <div className="text-xs">
        {error && (
          <div className="mb-2 p-2 bg-red-900/50 text-red-200 rounded text-xs break-words whitespace-normal">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="mb-2 p-2 bg-blue-900/50 text-blue-200 rounded text-xs">
            Loading...
          </div>
        )}

        {!selectedObject && (
          <div className="mb-2 p-2 bg-yellow-900/50 text-yellow-200 rounded text-xs">
            No object selected
          </div>
        )}

        <GridDropdown
          label="Source"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          options={[
            { label: 'Uploaded Clip', value: 'Uploaded Clip' },
            { label: 'URL', value: 'URL' },
          ]}
        />

        <GridInputWithIcon
          label={source === 'URL' ? 'Video URL' : 'Video Clip'}
          icon={addcomponent.group306}
          value={videoClip}
          onChange={(e) => setVideoClip(e.target.value)}
          readOnly={source !== 'URL'}
          onClick={handleIconClick}
        />

        <EnhancedSlider
          toFix={2}
          label="Playback Speed"
          value={value}
          onChange={setValue}
          min={0.25}
          max={2}
          step={0.25}
          unit=""
        />
        <EnhancedSlider
          toFix={2}
          label="Volume"
          value={volume}
          onChange={setVolume}
          min={0}
          max={1}
          step={0.05}
          unit=""
        />

        <GridDropdown
          label="Playback Settings"
          value={playbackState}
          onChange={(e) => setPlaybackState(e.target.value)}
          options={[
            { label: 'Play', value: 'Play' },
            { label: 'Pause', value: 'Pause' },
            { label: 'Stop', value: 'Stop' },
          ]}
        />

        <ToggleCheckbox
          label="Play on Awake"
          checked={playOnAwakeChecked}
          onToggle={() => setPlayOnAwakeChecked(!playOnAwakeChecked)}
        />

        <ToggleCheckbox
          label="Wait for First Frame"
          checked={waitForFirstFrameChecked}
          onToggle={() =>
            setWaitForFirstFrameChecked(!waitForFirstFrameChecked)
          }
        />

        <ToggleCheckbox
          label="Loop"
          checked={loopChecked}
          onToggle={() => setLoopChecked(!loopChecked)}
        />

        <ToggleCheckbox
          label="Mute"
          checked={muteChecked}
          onToggle={() => setMuteChecked(!muteChecked)}
        />
        
        <GridDropdown
          label="Render Mode"
          value={renderMode}
          onChange={(e) => setRenderMode(e.target.value)}
          options={[
            { label: 'Render Texture', value: 'Render Texture' },
            { label: 'Screen Space', value: 'Screen Space' },
            { label: 'World Space', value: 'World Space' },
          ]}
        />

        <GridDropdown
          label="Aspect Ratio"
          value={aspectRatio}
          onChange={(e) => setAspectRatio(e.target.value)}
          menuClassName="homepage-scrollbar"
          options={[
            { label: 'Fit Horizontally', value: 'Fit Horizontally' },
            { label: 'Fit Vertically', value: 'Fit Vertically' },
            { label: 'Stretch', value: 'Stretch' },
          ]}
        />

        <div className="mt-2 pt-2 border-t border-gray-700">
          <ToggleCheckbox
            label="Chroma Key"
            checked={chromaKeyEnabled}
            onToggle={() => setChromaKeyEnabled(!chromaKeyEnabled)}
          />

          {chromaKeyEnabled && (
            <>
              <GridDropdown
                label="Chroma Key Color Preset"
                value={chromaKeyColor}
                onChange={(e) => {
                  const preset = e.target.value;
                  setChromaKeyColor(preset);
                  // Update custom color when preset is selected
                  if (preset === 'Green') {
                    setChromaKeyCustomColor('#00FF00');
                  } else if (preset === 'Blue') {
                    setChromaKeyCustomColor('#0000FF');
                  }
                }}
                options={[
                  { label: 'Green', value: 'Green' },
                  { label: 'Blue', value: 'Blue' },
                  { label: 'Custom', value: 'Custom' },
                ]}
              />

              <ColorInput
                label="Select Color to Remove"
                color={chromaKeyCustomColor}
                setColor={(newColor) => {
                  setChromaKeyCustomColor(newColor);
                  // Auto-set to Custom when user picks a color
                  if (chromaKeyColor !== 'Custom') {
                    setChromaKeyColor('Custom');
                  }
                }}
              />

              <EnhancedSlider
                toFix={3}
                label="Similarity"
                value={chromaKeySimilarity}
                onChange={setChromaKeySimilarity}
                min={0.001}
                max={0.1}
                step={0.001}
                unit=""
              />

              <EnhancedSlider
                toFix={2}
                label="Smoothness"
                value={chromaKeySmoothness}
                onChange={setChromaKeySmoothness}
                min={0}
                max={0.5}
                step={0.01}
                unit=""
              />

              <EnhancedSlider
                toFix={2}
                label="Spill"
                value={chromaKeySpill}
                onChange={setChromaKeySpill}
                min={0}
                max={0.5}
                step={0.01}
                unit=""
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoComponent;
